import environments from '@app/configs/environments';
import { Page } from '@app/models/dtos/page';
import { WorkspaceInvitationDto, WorkspaceMembersDto } from '@app/models/dtos/workspace-member-dto';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const WORKSPACE_INVITATIONS_PATH = 'membersNInvitationsApi';

const WORKSPACE_INVITATIONS_TAG = 'WORKSPACE_INVITATIONS_TAG';
const WORKSPACE_MEMBERS_TAG = 'WORKSPACE_MEMBERS_TAG';

export const membersNInvitationsApi = createApi({
    reducerPath: WORKSPACE_INVITATIONS_PATH,
    tagTypes: [WORKSPACE_INVITATIONS_TAG, WORKSPACE_MEMBERS_TAG],
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    refetchOnFocus: true,
    keepUnusedDataFor: 0,
    baseQuery: fetchBaseQuery({
        baseUrl: environments.API_ENDPOINT_HOST + '/workspaces',
        prepareHeaders(headers) {
            headers.set('Access-Control-Allow-Origin', environments.API_ENDPOINT_HOST);
            return headers;
        },
        credentials: 'include'
    }),
    endpoints: (builder) => ({
        respondToWorkspaceInvitation: builder.mutation<any, any>({
            query: (request) => ({
                url: `/${request.workspaceId}/members/invitations/${request.invitationToken}`,
                method: 'POST',
                params: {
                    response_status: request.responseStatus
                }
            })
        }),
        getWorkspaceMembers: builder.query<Array<WorkspaceMembersDto>, any>({
            query: (request) => ({
                url: `/${request.workspaceId}/members`,
                method: 'GET'
            }),
            providesTags: [WORKSPACE_MEMBERS_TAG]
        }),
        deleteWorkspaceMember: builder.mutation<any, any>({
            query: (request) => ({
                url: `/${request.workspaceId}/members/${request.userId}`,
                method: 'DELETE'
            }),
            invalidatesTags: [WORKSPACE_MEMBERS_TAG, WORKSPACE_INVITATIONS_TAG]
        }),
        deleteWorkspaceInvitation: builder.mutation<any, any>({
            query: (request) => ({
                url: `/${request.workspaceId}/members/invitations/${request.invitationToken}`,
                method: 'DELETE'
            }),
            invalidatesTags: [WORKSPACE_INVITATIONS_TAG]
        }),
        getWorkspaceMembersInvitations: builder.query<Page<WorkspaceInvitationDto>, any>({
            query: (request) => ({
                url: `/${request.workspaceId}/members/invitations`,
                method: 'GET'
            }),
            providesTags: [WORKSPACE_INVITATIONS_TAG]
        }),
        getWorkspaceInvitation: builder.query<WorkspaceInvitationDto, { workspaceId: string; invitationToken: string }>({
            query: (request) => ({
                url: `/${request.workspaceId}/members/invitations/${request.invitationToken}`,
                method: 'GET'
            }),
            providesTags: [WORKSPACE_INVITATIONS_TAG]
        }),
        // `locale`: the invitation mail's language ("en" or "nl"; English when unset)
        inviteToWorkspace: builder.mutation<any, any>({
            query: (request) => ({
                url: `/${request.workspaceId}/members/invitations`,
                method: 'POST',
                body: request.body,
                ...(request.locale ? { params: { locale: request.locale } } : {})
            }),
            invalidatesTags: [WORKSPACE_INVITATIONS_TAG]
        }),
        updateWorkspaceMemberRole: builder.mutation<WorkspaceMembersDto, { workspaceId: string; userId: string; role: string }>({
            query: ({ workspaceId, userId, role }) => ({
                url: `/${workspaceId}/members/${userId}`,
                method: 'PATCH',
                body: { role }
            }),
            invalidatesTags: [WORKSPACE_MEMBERS_TAG]
        }),
        // Another owner becomes the billing owner (the plan's account); the
        // previous billing owner stays an owner.
        // An owner removes a member's authenticator app (204): their sessions end and they are emailed.
        resetMemberTotp: builder.mutation<void, { workspaceId: string; userId: string }>({
            query: (request) => ({
                url: `/${request.workspaceId}/members/${request.userId}/totp`,
                method: 'DELETE'
            }),
            invalidatesTags: [WORKSPACE_MEMBERS_TAG]
        }),
        makeBillingOwner: builder.mutation<{ message: string; ownerId: string }, { workspaceId: string; userId: string }>({
            query: ({ workspaceId, userId }) => ({
                url: `/${workspaceId}/members/${userId}/make-billing-owner`,
                method: 'POST'
            }),
            invalidatesTags: [WORKSPACE_MEMBERS_TAG]
        }),
        // `locale`: the mail's language, as for a new invitation ("en" or "nl")
        resendWorkspaceInvitation: builder.mutation<any, any>({
            query: (request) => ({
                url: `/${request.workspaceId}/members/invitations/${request.invitationToken}/resend`,
                method: 'POST',
                body: request.body,
                ...(request.locale ? { params: { locale: request.locale } } : {})
            }),
            invalidatesTags: [WORKSPACE_INVITATIONS_TAG]
        })
        // // Resend invitation mutation
        // resendWorkspaceInvitation: builder.mutation<any, { workspaceId: string; invitationId: string }>({
        //     query: ({ workspaceId, invitationId }) => ({
        //         url: `/${workspaceId}/members/invitations/${invitationId}/resend`,
        //         method: 'POST'
        //     }),
        //     invalidatesTags: [WORKSPACE_INVITATIONS_TAG]
        // })
    })
});

export const {
    useResetMemberTotpMutation,
    useRespondToWorkspaceInvitationMutation,
    useGetWorkspaceMembersQuery,
    useGetWorkspaceMembersInvitationsQuery,
    useGetWorkspaceInvitationQuery,
    useInviteToWorkspaceMutation,
    useDeleteWorkspaceMemberMutation,
    useDeleteWorkspaceInvitationMutation,
    useResendWorkspaceInvitationMutation, // Export the resend mutation
    useUpdateWorkspaceMemberRoleMutation,
    useMakeBillingOwnerMutation
} = membersNInvitationsApi;
